import { ReactNode } from "react";
import { cn } from "@drpcrd/utils/shared";
import { cva } from "class-variance-authority";
import { Typography } from "./typography";
import { colors } from "../styles/colors";

const iconContainerVariants = cva(
  "flex h-6 w-6 max-h-6 max-w-6 shrink-0 items-center justify-center rounded-full transition-colors",
  {
    variants: {
      status: {
        completed: "bg-success-500 text-white",
        current: "bg-accent text-white",
        upcoming: "bg-neutral-200 text-neutral-400",
        error: "bg-error-500 text-white",
      },
    },
    defaultVariants: {
      status: "upcoming",
    },
  },
);

const contentVariants = cva("flex flex-col gap-0", {
  variants: {
    status: {
      completed: "",
      current: "",
      upcoming: "opacity-60",
      error: "",
    },
  },
  defaultVariants: {
    status: "upcoming",
  },
});

type StepStatus = "completed" | "current" | "upcoming" | "error";

type StepProps = {
  status?: StepStatus;
  icon: ReactNode;
  title: string;
  description?: string;
  content?: ReactNode;
  showLine?: boolean;
  className?: string;
};

/**
 * Individual step component with icon, title, description, and connecting line.
 */
const VerticalProgressionStep = ({
  status = "upcoming",
  icon,
  title,
  description,
  content,
  showLine = true,
  className,
}: StepProps) => (
  <div className={cn("relative flex w-full gap-2", className)}>
    {/* Icon Container with Line */}
    <div className="relative">
      <div className={iconContainerVariants({ status })}>{icon}</div>
      {showLine && (
        <div
          className="absolute top-6 left-[23px] h-full w-[2px]"
          style={{
            backgroundImage: `repeating-linear-gradient(to bottom, ${colors.primary200} 0px, ${colors.primary200} 6px, transparent 6px, transparent 12px)`,
            backgroundRepeat: "repeat",
          }}
        />
      )}
    </div>

    {/* Content */}
    <div className={cn("w-full", contentVariants({ status }))}>
      {/* Title */}
      <Typography weight="semibold">{title}</Typography>

      {/* Description */}
      {description && (
        <Typography size="sm" color="neutral" className="mb-2">
          {description}
        </Typography>
      )}

      {/* Additional Content */}
      {content && <div>{content}</div>}
    </div>
  </div>
);

type Props = {
  children: ReactNode;
  className?: string;
};

/**
 * Vertical progression component for displaying step-by-step status flows.
 * Auto-connects steps with vertical lines and supports different status states.
 *
 * @example
 * ```tsx
 * <VerticalProgression>
 *   <VerticalProgression.Step
 *     status="completed"
 *     title="Pitch submitted"
 *     description="Your pitch has been received"
 *   />
 *   <VerticalProgression.Step
 *     status="current"
 *     title="Under review"
 *     description="Our team is reviewing your pitch"
 *   />
 *   <VerticalProgression.Step
 *     status="upcoming"
 *     title="Sent to brand"
 *     description="Your pitch will be sent to the brand"
 *   />
 * </VerticalProgression>
 * ```
 */
export const VerticalProgression = ({ children, className }: Props) => (
  <div className={cn("flex flex-col gap-3", className)}>{children}</div>
);

VerticalProgression.Step = VerticalProgressionStep;

export type { StepStatus, StepProps };
